import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabase';
import { CoinIcon, ArrowLeftIcon } from '../components/icons/NavIcons';
import CreditBenefitsInfo from '../components/CreditBenefitsInfo';
import LoadingSpinner from '../components/LoadingSpinner';
import PageHeader from '../components/PageHeader';

const CreditRechargePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [creditAmount, setCreditAmount] = useState(null);
  const [packages, setPackages] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [discountStart, setDiscountStart] = useState(0);
  const [acceptTerms, setAcceptTerms] = useState(false);

  // Détection de l'environnement local (GitHub Codespace)
  const isCodespace = window.location.hostname.includes('app.github.dev');
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const isLocal = isCodespace || isLocalhost;
  
  const getApiUrl = () => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    return `${supabaseUrl}/functions/v1/stripe-checkout`;
  };

  useEffect(() => {
    // Rediriger vers les paramètres si non authentifié
    if (!user) {
      navigate('/settings');
      return;
    }

    // Récupérer la valeur du crédit et les packages depuis parameters
    const fetchCreditData = async () => {
      try {
        // Récupérer credit_amount, discount, discount_start et tous les credit_package_*
        const { data, error } = await supabase
          .from('parameters')
          .select('parameter, value')
          .or('parameter.eq.credit_amount,parameter.eq.discount,parameter.eq.discount_start,parameter.like.credit_package_%');

        console.log('Données récupérées:', data);

        if (error) throw error;

        if (data) {
          // Extraire credit_amount
          const creditAmountParam = data.find(p => p.parameter === 'credit_amount');
          const creditValue = creditAmountParam ? parseFloat(creditAmountParam.value) : 1;
          setCreditAmount(creditValue);

          // Extraire discount et discount_start
          const discountParam = data.find(p => p.parameter === 'discount');
          const discountValue = discountParam ? parseFloat(discountParam.value) : 0;
          setDiscount(discountValue);

          const discountStartParam = data.find(p => p.parameter === 'discount_start');
          const discountStartValue = discountStartParam ? parseFloat(discountStartParam.value) : 0;
          setDiscountStart(discountStartValue);

          // Extraire et trier les packages
          const packageData = data
            .filter(p => p.parameter.startsWith('credit_package_'))
            .map(p => {
              const amount = parseInt(p.parameter.replace('credit_package_', ''));
              const appliedDiscount = amount >= discountStartValue ? discountValue : 0;
              const price = parseFloat((amount * creditValue * (1 - appliedDiscount / 100)).toFixed(2));
              
              return { amount, price, discount: appliedDiscount };
            })
            .sort((a, b) => a.amount - b.amount);

          // Marquer le package du milieu comme populaire
          if (packageData.length > 0) {
            const middleIndex = Math.floor(packageData.length / 2);
            packageData[middleIndex].popular = true;
          }

          setPackages(packageData);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des paramètres:', error);
        // Valeurs par défaut en cas d'erreur
        setCreditAmount(1);
        setDiscount(0);
        setDiscountStart(0);
        setPackages([
          { amount: 1, price: 2, popular: false },
          { amount: 10, price: 9, popular: true },
          { amount: 50, price: 40, popular: false },
        ]);
      }
    };

    fetchCreditData();
  }, [user, navigate]);

  const handlePurchase = async (packageItem) => {
    if (!user) {
      alert(t('credits.loginRequired'));
      return;
    }

    if (!acceptTerms) {
      alert(t('terms.mustAcceptTerms'));
      return;
    }

    setLoading(true);
    try {
      console.log('=== DÉBUT CRÉATION SESSION PAIEMENT ===');
      console.log('Package sélectionné:', packageItem);
      console.log('User ID:', user.id);
      console.log('User complet:', user);
      
      // Logs de vérification d'environnement
      console.log('=== VÉRIFICATION ENVIRONNEMENT ===');
      console.log('window.location.hostname:', window.location.hostname);
      console.log('isCodespace:', isCodespace);
      console.log('isLocalhost:', isLocalhost);
      console.log('isLocal:', isLocal);
      console.log('Environnement détecté:', isLocal ? 'LOCAL' : 'PRODUCTION');

      // Récupérer le token de session pour l'authentification
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Session non disponible');
      }

      const stripeCheckoutUrl = getApiUrl();
      console.log('=== CONFIGURATION API ===');
      console.log('URL complète appelée:', stripeCheckoutUrl);
      console.log('Méthode:', 'POST');
      console.log('Headers:', {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer [TOKEN]',
      });
      
      const requestBody = {
        amount: packageItem.price,
        credits: packageItem.amount,
        userId: user.id,
      };
      
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      };
      console.log('Body de la requête:', requestBody);
      console.log('Body stringifié:', JSON.stringify(requestBody));

      // Vérifier que l'URL est correcte
      console.log('=== TEST ACCESSIBILITÉ URL ===');
      console.log('URL accessible ?', stripeCheckoutUrl);
      console.log('Protocole:', new URL(stripeCheckoutUrl).protocol);
      console.log('Host:', new URL(stripeCheckoutUrl).host);
      console.log('Pathname:', new URL(stripeCheckoutUrl).pathname);

      console.log('=== ENVOI DE LA REQUÊTE ===');
      const startTime = performance.now();
      
      // Appeler la fonction Edge Supabase
      const response = await fetch(stripeCheckoutUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody),
      });

      const endTime = performance.now();
      console.log(`=== RÉPONSE REÇUE (en ${(endTime - startTime).toFixed(2)}ms) ===`);
      console.log('Statut:', response.status);
      console.log('Status Text:', response.statusText);
      console.log('Response OK:', response.ok);
      console.log('Response Type:', response.type);
      console.log('Response URL:', response.url);
      console.log('Headers:', Object.fromEntries(response.headers.entries()));

      // Gérer spécifiquement l'erreur 404
      if (response.status === 404) {
        console.error('=== ERREUR 404 ===');
        console.error('L\'endpoint n\'existe pas ou n\'est pas accessible');
        console.error('Vérifiez que la fonction Azure est bien déployée sur:', stripeCheckoutUrl);
        console.error('Environnement actuel:', isLocal ? 'LOCAL (dev)' : 'PRODUCTION');
        throw new Error('Service de paiement non disponible. Veuillez contacter le support.');
      }

      // Vérifier si la réponse contient du JSON
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
        console.log('Réponse de l\'API:', data);
      } else {
        // Si ce n'est pas du JSON, lire le texte brut
        const text = await response.text();
        console.log('Réponse brute de l\'API:', text);
        
        if (!response.ok) {
          throw new Error(`Erreur ${response.status}: ${text || 'Réponse vide'}`);
        }
        
        // Tenter de parser manuellement si le texte ressemble à du JSON
        try {
          data = text ? JSON.parse(text) : {};
        } catch (e) {
          throw new Error(`Réponse invalide du serveur: ${text}`);
        }
      }

      if (!response.ok) {
        const errorMessage = data?.error || data?.message || `Erreur ${response.status}`;
        throw new Error(errorMessage);
      }

      if (data?.url) {
        // Rediriger vers Stripe Checkout
        window.location.href = data.url;
      } else {
        throw new Error('URL de paiement manquante dans la réponse');
      }
    } catch (error) {
      console.error('=== ERREUR COMPLÈTE ===');
      console.error('Erreur lors de la création de la session de paiement:', error);
      console.error('Type d\'erreur:', error.constructor.name);
      console.error('Message:', error.message);
      console.error('Stack:', error.stack);
      
      // Message d'erreur plus détaillé
      let errorMessage = t('credits.paymentError');
      if (error.message) {
        errorMessage += `: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Afficher un loader si l'utilisateur n'est pas encore chargé
  if (!user) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PageHeader title={t('credits.recharge.title')} backPath="/settings" />

      <div className="p-4">
        <CreditBenefitsInfo />

        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-col items-center justify-center gap-2">
            {creditAmount && (
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold text-bleu2">
                  1
                </span>
                <CoinIcon className="w-8 h-8" />
                <span className="text-lg font-semibold text-bleu2">
                  = {creditAmount} €
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Acceptation des CGU */}
        <div className="bg-white rounded-lg shadow p-4 mb-6 border-t">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              required
              className="w-5 h-5 mt-0.5 text-bleu1 border-gray-300 rounded focus:ring-bleu1 accent-bleu1 flex-shrink-0"
            />
            <span className="text-sm text-gray-700">
              {t('terms.acceptTerms')} *{' '}
              <a
                href="/settings/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-bleu1 hover:underline"
              >
                {t('settings.help.terms')}
              </a>
            </span>
          </label>
        </div>

        <div className="space-y-4">
          {packages.map((pkg, index) => (
            <div
              key={index}
              className={`bg-white p-6 rounded-lg shadow-sm border-2 ${
                pkg.popular ? 'border-[#2894D8]' : 'border-gray-200'
              } relative`}
            >
              {pkg.popular && (
                <span className="absolute top-2 right-2 bg-[#2894D8] text-white text-xs px-2 py-1 rounded-full">
                  {t('credits.recharge.popular')}
                </span>
              )}
              
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <CoinIcon className="w-8 h-8 mr-3" />
                  <div>
                    <h3 className="text-h3 font-bold">{pkg.amount} {t('credits.credits')}</h3>
                    {pkg.discount > 0 && (
                      <p className="text-sm text-green-600">
                        {t('credits.recharge.save', { 
                          amount: pkg.discount.toFixed(0)
                        })}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold">{pkg.price}€</p>
                </div>
              </div>

              <button
                onClick={() => handlePurchase(pkg)}
                disabled={loading || !acceptTerms}
                className="w-full bg-[#2894D8] text-white py-3 rounded-lg font-medium hover:bg-[#2380c0] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('common.loading') : t('credits.recharge.buy')}
              </button>
            </div>
          ))}
        </div>
        
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/settings')}
            className="text-gray-600 hover:text-gray-800"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  ); 
};

export default CreditRechargePage;
